/**
 * Shared Canvas2D paint primitives for mobile's chart set (Expo web only —
 * native uses Skia, see the sibling `*.native.tsx` renderers in `mobile/`).
 *
 * This is the mobile-side counterpart to web's
 * `web/src/components/room/charts/chartCanvas.ts`: same idea (stop
 * hand-rolling canvas setup + axis-grid loops per chart), deliberately
 * NOT shared code with it — mobile's Graphs tab is a light card, web's
 * dock is dark chrome (see `../draw.ts`'s header comment). Two intentional
 * themes over the same geometry, not duplicated logic to unify.
 *
 * Every draw*Canvas function in this directory should build its frame via
 * `setupCanvas`, paint its axis grid via `drawLogFreqAxisGrid` (or
 * `drawLinearAxisGrid` for a linear-time-domain chart like impulse/step
 * response — heatmaps/echogram/waterfall still have their own tick loops,
 * neither grid helper applies to those), and paint series via
 * `drawSeriesLine` / `drawBars` / `drawCells`. Pick a chart-specific ink from `INK` below rather than
 * inventing a new colour — the existing three (`drawFreqResponseCanvas`,
 * `drawRt60Canvas`, `drawAbsorptionCanvas`) do not use this module (they
 * predate it and are already colour-consistent with `INK.primary`/
 * `INK.gridMajor`/`INK.gridMinor` by construction) — don't refactor them
 * as part of adding a new chart, that's a separate, lower-value cleanup.
 */

import { canvasTypeFont } from '@roomtreatment/design';
import type { PlotFrame } from '../layout';
import type { AxisTicks, YTick } from '../geometry';
import type { PlotPoint } from '../series';

/** The one ink palette every new mobile chart in this directory draws
 *  with. Matches the existing FR/RT60 mobile convention (`#3A6CA8`
 *  primary, `rgba(23,32,58,x)` grid/neutral ink) rather than web dock's
 *  `designTokens`/`canvasInk` — see module doc. */
export const INK = {
  primary: '#3A6CA8',
  gridMajor: 'rgba(23,32,58,0.14)',
  gridMinor: 'rgba(23,32,58,0.06)',
  label: '#6B778C',
  /** Bonello-OK / good-STI bars, RT60 target band. */
  good: 'rgba(46,125,50,0.65)',
  /** Bonello-violation / poor-STI bars. */
  warn: 'rgba(198,40,40,0.6)',
  /** Neutral bar fill (RTA, STI-unknown). */
  neutralBar: 'rgba(23,32,58,0.62)',
  /** Dashed reference lines (Schroeder frequency, etc). */
  referenceDashed: 'rgba(23,32,58,0.5)',
} as const;

/** Cycling dash pattern for a chart that overlays several unlabelled-by-hue
 *  series (per-speaker FR/phase, decay-fit comparison, clarity) — same
 *  "line style, not colour, tells series apart" convention as web. Index
 *  by series/method position; wraps past its length. */
export const DASH_CYCLE: number[][] = [[], [5, 3], [1, 3], [8, 3, 2, 3], [2, 2]];

export interface CanvasSetup {
  ctx: CanvasRenderingContext2D;
  W: number;
  H: number;
}

/** DPR-scaled canvas backing store sized to the CSS width/height the
 *  caller measured (via ChartSlot). Sets the tick-label font before any
 *  geometry call measures text against it — same ordering requirement as
 *  `packages/charts/src/draw.ts`'s private `setup()`. */
export function setupCanvas(canvas: HTMLCanvasElement, cssW: number, cssH: number): CanvasSetup | null {
  const W = Math.max(1, Math.floor(cssW));
  const H = Math.max(1, Math.floor(cssH));
  const dpr = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1;
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  canvas.style.width = `${W}px`;
  canvas.style.height = `${H}px`;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, W, H);
  ctx.font = canvasTypeFont('tick');
  return { ctx, W, H };
}

export function drawEmptyMessage(ctx: CanvasRenderingContext2D, W: number, H: number, text: string): void {
  ctx.fillStyle = INK.label;
  ctx.textAlign = 'center';
  ctx.font = canvasTypeFont('label');
  ctx.fillText(text, W / 2, H / 2);
}

export interface LogFreqGridOpts {
  gridMajor?: string;
  gridMinor?: string;
  xDistinguishMajor?: boolean;
  yDistinguishMajor?: boolean;
}

/** Log-frequency x-axis + linear y-axis grid + tick labels — the frame
 *  every log-freq chart in this directory draws before its own series
 *  (RTA, modal density, phase, group delay, per-speaker FR/phase, clarity,
 *  decay-fit comparison, approx STI). Heatmaps (spectrogram, spl-
 *  uniformity), the time-domain charts (waterfall, echogram, impulse/step
 *  response) and cascade views have their own tick loops — don't force
 *  this in for those. */
export function drawLogFreqAxisGrid(
  ctx: CanvasRenderingContext2D,
  frame: PlotFrame,
  xTicks: AxisTicks,
  yTicks: YTick[],
  opts: LogFreqGridOpts = {},
): void {
  const {
    gridMajor = INK.gridMajor,
    gridMinor = INK.gridMinor,
    xDistinguishMajor = true,
    yDistinguishMajor = true,
  } = opts;
  ctx.textAlign = 'right';
  for (const t of yTicks) {
    const y = frame.plotY + t.y;
    ctx.strokeStyle = yDistinguishMajor && t.major ? gridMajor : gridMinor;
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(frame.plotX, y); ctx.lineTo(frame.plotX + frame.plotW, y); ctx.stroke();
    ctx.fillStyle = INK.label;
    ctx.fillText(t.label, frame.plotX - 4, y + 3);
  }
  for (const t of xTicks.all) {
    const x = frame.plotX + t.x;
    ctx.strokeStyle = xDistinguishMajor && t.major ? gridMajor : gridMinor;
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(x, frame.plotY); ctx.lineTo(x, frame.plotY + frame.plotH); ctx.stroke();
  }
  ctx.textAlign = 'center';
  ctx.fillStyle = INK.label;
  for (const t of xTicks.labeled) {
    ctx.fillText(t.label, frame.plotX + t.x, frame.plotY + frame.plotH + 12);
  }
}

export interface LinearXTick {
  value: number;
  x: number;
  label: string;
}

export interface LinearGridOpts {
  gridMajor?: string;
  gridMinor?: string;
}

/** Linear x-axis (e.g. time, ms) + linear y-axis grid + tick labels — for
 *  charts whose x domain is NOT log-frequency (impulse response, step
 *  response, and any future time-domain chart). `xTicks` here is the
 *  simpler `{value,x,label}[]` shape `computeImpulseResponseGeometry`
 *  returns (every x tick is labeled, no major/minor split) — NOT the
 *  `AxisTicks` `{all,labeled}` shape `drawLogFreqAxisGrid` takes. `yTicks`
 *  reuses the same `YTick` shape as the log-freq charts. */
export function drawLinearAxisGrid(
  ctx: CanvasRenderingContext2D,
  frame: PlotFrame,
  xTicks: LinearXTick[],
  yTicks: YTick[],
  opts: LinearGridOpts = {},
): void {
  const { gridMajor = INK.gridMajor, gridMinor = INK.gridMinor } = opts;
  ctx.textAlign = 'right';
  for (const t of yTicks) {
    const y = frame.plotY + t.y;
    ctx.strokeStyle = t.major ? gridMajor : gridMinor;
    ctx.lineWidth = t.major ? 1.3 : 1;
    ctx.beginPath(); ctx.moveTo(frame.plotX, y); ctx.lineTo(frame.plotX + frame.plotW, y); ctx.stroke();
    ctx.fillStyle = INK.label;
    ctx.fillText(t.label, frame.plotX - 4, y + 3);
  }
  ctx.textAlign = 'center';
  ctx.fillStyle = INK.label;
  for (const t of xTicks) {
    const x = frame.plotX + t.x;
    ctx.strokeStyle = gridMinor;
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(x, frame.plotY); ctx.lineTo(x, frame.plotY + frame.plotH); ctx.stroke();
    ctx.fillText(t.label, x, frame.plotY + frame.plotH + 12);
  }
}

/** One stroked polyline, optionally dashed — reset `setLineDash([])` after
 *  so callers never leak a dash state into the next draw call. */
export function drawSeriesLine(
  ctx: CanvasRenderingContext2D,
  frame: PlotFrame,
  points: PlotPoint[],
  color: string,
  width: number,
  dash: number[] = [],
): void {
  if (points.length < 2) return;
  ctx.setLineDash(dash);
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(frame.plotX + points[0]!.x, frame.plotY + points[0]!.y);
  for (let i = 1; i < points.length; i++) ctx.lineTo(frame.plotX + points[i]!.x, frame.plotY + points[i]!.y);
  ctx.stroke();
  ctx.setLineDash([]);
}

export interface DrawBar {
  x0: number;
  x1: number;
  y: number;
  color: string;
}

/** Filled bars from plot-local geometry (RTA, modal density, approx STI).
 *  Caller decides colour per bar (quality/violation tinting) — this only
 *  paints. `bottom` is the plot's baseline (`frame.plotY + frame.plotH`). */
export function drawBars(ctx: CanvasRenderingContext2D, frame: PlotFrame, bars: DrawBar[], gapPx = 1): void {
  const bottom = frame.plotY + frame.plotH;
  for (const bar of bars) {
    const x0 = frame.plotX + bar.x0 + gapPx / 2;
    const x1 = frame.plotX + bar.x1 - gapPx / 2;
    const y = frame.plotY + bar.y;
    if (x1 <= x0) continue;
    ctx.fillStyle = bar.color;
    ctx.fillRect(x0, y, x1 - x0, bottom - y);
  }
}

export interface DrawCell {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  color: string | null;
}

/** Filled heatmap cells (spectrogram, SPL uniformity). A `null` colour
 *  means "masked, leave transparent" — matches web's convention for
 *  non-air / no-data cells. */
export function drawCells(ctx: CanvasRenderingContext2D, frame: PlotFrame, cells: DrawCell[]): void {
  for (const cell of cells) {
    if (cell.color == null) continue;
    ctx.fillStyle = cell.color;
    ctx.fillRect(frame.plotX + cell.x0, frame.plotY + cell.y0, cell.x1 - cell.x0, cell.y1 - cell.y0);
  }
}

/** `alpha ramp for a 0..-rangeDb intensity` — 0 dB (loudest/best) = ink at
 *  full opacity, `-rangeDb` or below = paper. Shared formula for
 *  spectrogram (`rangeDb=60`) and SPL uniformity (`rangeDb=20`) so both
 *  heatmaps read on the same visual scale convention as web. */
export function intensityAlpha(levelDb: number, rangeDb: number, minAlpha = 0.02): number {
  return Math.max(minAlpha, 1 - Math.min(1, -levelDb / rangeDb));
}

export function withAlpha(hexOrRgb: string, alpha: number): string {
  // INK constants here are either '#rrggbb' or already 'rgba(...)'. Only
  // the '#rrggbb' primary needs conversion; reuse it for intensity ramps.
  if (hexOrRgb.startsWith('#')) {
    const r = parseInt(hexOrRgb.slice(1, 3), 16);
    const g = parseInt(hexOrRgb.slice(3, 5), 16);
    const b = parseInt(hexOrRgb.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  return hexOrRgb.replace(/[\d.]+\)$/, `${alpha})`);
}
